// ShiftShedule_Api_service.js - API utilities for onboarding operations

// IMPORTANT: You will need to import fetchAuthSession from aws-amplify/auth
// Ensure you have `aws-amplify` installed and configured in your project (e.g., in index.js)
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 * It's crucial that a user is logged in for this to return a valid token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    // The ID Token is typically what API Gateway expects for authorization.
    if (tokens && tokens.idToken) {
      // Check if token is expired
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for onboarding API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Function to get all sites from the API
 * @returns {Promise<Object>} Response object containing sites data
 */
export const getAllSites = async () => {
  try {
    const authToken = await getAuthToken();
    
    if (!authToken) {
      throw new Error('Authentication token not available');
    }

    const response = await fetch(`${API_BASE_URL}/site/getall`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Successfully fetched all sites:', data);
    return data;
    
  } catch (error) {
    console.error('Error fetching all sites:', error);
    throw error;
  }
};

/**
 * Function to get all shifts from the API
 * @returns {Promise<Object>} Response object containing shifts data
 */
export const getAllShifts = async () => {
  try {
    const authToken = await getAuthToken();
    
    if (!authToken) {
      throw new Error('Authentication token not available');
    }

    const response = await fetch(`${API_BASE_URL}/shift`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Successfully fetched all shifts:', data);
    return data;
    
  } catch (error) {
    console.error('Error fetching all shifts:', error);
    throw error;
  }
};

/**
 * Function to get shift roster from the API
 * @param {string} tenantSiteId - The tenant site ID
 * @param {string} date - The target date in YYYY-MM-DD format
 * @returns {Promise<Object>} Response object containing shift roster data
 */
export const getShiftRoster = async (tenantSiteId, date) => {
  try {
    const authToken = await getAuthToken();
    
    if (!authToken) {
      throw new Error('Authentication token not available');
    }

    // Validate required parameters
    if (!tenantSiteId || !date) {
      throw new Error('tenantSiteId and date are required parameters');
    }

    // Build query string parameters
    const queryParams = new URLSearchParams({
      tenant_site_id: tenantSiteId,
      date: date
    });

    const response = await fetch(`${API_BASE_URL}/shiftroaster?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('No roster details found for the specified site and date');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Successfully fetched shift roster:', data);
    return data;
    
  } catch (error) {
    console.error('Error fetching shift roster:', error);
    throw error;
  }
};

/**
 * Sends shift data to the API for creating or updating shifts.
 * Uses the same endpoint for both create and update operations (like Flutter implementation).
 * @param {string} siteId - The ID of the site
 * @param {Object} data - The shift data to send
 * @returns {Promise<Object>} API response
 */
export const sendShiftData = async (siteId, data) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Authentication token not found');
    }

    // Clean and prepare data for API submission (same as Flutter)
    const cleanedData = { ...data };
    
    // Remove timestamp fields from the main data object as per Flutter implementation
    delete cleanedData.shift_start_timestamp;
    delete cleanedData.shift_end_timestamp;
    
    // Clean timestamp fields from individual shifts
    if (cleanedData.shifts && Array.isArray(cleanedData.shifts)) {
      cleanedData.shifts = cleanedData.shifts.map(shift => {
        const cleanedShift = { ...shift };
        delete cleanedShift.shift_start_timestamp;
        delete cleanedShift.shift_end_timestamp;
        return cleanedShift;
      });
    }

    // Check if this is an update operation by looking for ss_id in the shifts
    const hasExistingShift = data.shifts && data.shifts.some(shift => 
      shift.ss_id && 
      shift.ss_id.toString().trim() !== '' && 
      !shift.ss_id.startsWith('default_shift_') && 
      !shift.ss_id.startsWith('new_shift_')
    );

    console.log(hasExistingShift ? 'Updating existing shift with ss_id...' : 'Creating new shift...');
    console.log('Sending shift data to API:', cleanedData);

    const url = `${API_BASE_URL}/shiftroaster/add/v2`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cleanedData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('API Error:', response.status, response.statusText, errorData);
      throw new Error(`API Error: ${response.status} - ${errorData.message || response.statusText}`);
    }

    const responseData = await response.json();
    console.log('Successfully sent shift data:', responseData);
    return responseData;
    
  } catch (error) {
    console.error('Error sending shift data:', error);
    throw error;
  }
};


/**
 * Deletes a shift roster from the API.
 * DELETE /shiftroaster/delete
 * @param {string} ssId - The unique identifier of the shift schedule to delete
 * @returns {Promise<string>} Success message from the API
 */
export const deleteShiftRoster = async (ssId) => {
  try {
    const authToken = await getAuthToken();
    
    if (!authToken) {
      throw new Error('Authentication token not found');
    }

    // Validate required parameter
    if (!ssId) {
      throw new Error('ss_id is required for deleting shift roster');
    }

    const response = await fetch(`${API_BASE_URL}/shiftroaster/delete`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ss_id: ssId,
      }),
    });

    // Handle different response scenarios
    if (response.status === 200) {
      const responseText = await response.text();
      console.log('Successfully deleted shift roster:', responseText);
      return responseText; // Direct string message from Lambda
    } else {
      try {
        const body = await response.json();
        if (response.status === 400) {
          throw new Error(`Bad request: ${body.body || 'Missing ss_id'}`);
        } else {
          throw new Error(
            `Failed to delete shift: ${response.status} - ${body.body || response.statusText}`
          );
        }
      } catch (parseError) {
        throw new Error(
          `Failed to delete shift: ${response.status} - ${response.statusText}`
        );
      }
    }
    
  } catch (error) {
    console.error('Error deleting shift roster:', error);
    throw error;
  }
};